import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { TFunction } from 'i18next'
import { useAuth } from '../../hooks'
import { ApiError } from '../../services'

interface UseLoginFormResult {
  email: string
  setEmail: (value: string) => void
  password: string
  setPassword: (value: string) => void
  showPassword: boolean
  toggleShowPassword: () => void
  error: string | null
  isLoading: boolean
  handleSubmit: (e: React.FormEvent) => Promise<void>
}

export function useLoginForm(t: TFunction<'auth'>): UseLoginFormResult {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isLoading } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard'

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)

    if (!email || !password) {
      setError(t('login.validationError'))
      return
    }

    try {
      await login({ email, password })
      void navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('login.unexpectedError'))
    }
  }

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    toggleShowPassword: () => {
      setShowPassword((prev) => !prev)
    },
    error,
    isLoading,
    handleSubmit,
  }
}
